import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import type { AppointmentType, AppointmentTypeStatus, ChatBotBookingFlow, ConsultationCreationMode } from '../../calendar/types/Calendar';
import {
  createConsultationType,
  listConsultationTemplates,
  listConsultationTypes,
  updateConsultationType,
} from '../services/consultationService';
import type { ConsultationTemplate } from '../types/Consultation';
import styles from './ConsultationTypesPage.module.css';

type TypeEditorState = {
  name: string;
  defaultDurationMinutes: string;
  description: string;
  status: AppointmentTypeStatus;
  consultationCreationMode: ConsultationCreationMode;
  chatBotBookingFlow: ChatBotBookingFlow;
  chatBotInstruction: string;
};

const emptyEditorState = (): TypeEditorState => ({
  name: '',
  defaultDurationMinutes: '30',
  description: '',
  status: 'active',
  consultationCreationMode: 'single',
  chatBotBookingFlow: 'offerAvailableSlots',
  chatBotInstruction: '',
});

const getConsultationCreationModeLabel = (
  mode: ConsultationCreationMode,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  switch (mode) {
    case 'perContact':
      return t('consultationCreationModePerContact', { defaultValue: 'Per contact' });
    case 'perAsset':
      return t('consultationCreationModePerAsset', { defaultValue: 'Per asset' });
    case 'perEmployee':
      return t('consultationCreationModePerEmployee', { defaultValue: 'Per employee' });
    default:
      return t('consultationCreationModeSingle', { defaultValue: 'Single consultation' });
  }
};

const getChatBotBookingFlowLabel = (
  flow: ChatBotBookingFlow,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (flow === 'autoNearestSlot') {
    return t('chatBotBookingFlowAutoNearest', { defaultValue: 'Auto-book nearest slot' });
  }
  return t('chatBotBookingFlowOfferSlots', { defaultValue: 'Offer available slots' });
};

const toEditorState = (type: AppointmentType): TypeEditorState => ({
  name: type.name,
  defaultDurationMinutes: String(type.defaultDurationMinutes),
  description: type.description ?? '',
  status: type.status,
  consultationCreationMode: type.consultationCreationMode,
  chatBotBookingFlow: type.chatBotBookingFlow ?? 'offerAvailableSlots',
  chatBotInstruction: type.chatBotInstruction ?? '',
});

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const ConsultationTypesPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common']);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [types, setTypes] = React.useState<AppointmentType[]>([]);
  const [templates, setTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [editorState, setEditorState] = React.useState<TypeEditorState>(() => emptyEditorState());
  const [selectedTypeId, setSelectedTypeId] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | AppointmentTypeStatus>('all');
  const [usageFilter, setUsageFilter] = React.useState<'all' | 'linked' | 'unused'>('all');
  const [error, setError] = React.useState('');
  const [statusMessage, setStatusMessage] = React.useState('');

  const loadTypes = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [loadedTypes, loadedTemplates] = await Promise.all([
        listConsultationTypes(),
        listConsultationTemplates(),
      ]);
      const sorted = [...loadedTypes].sort((left, right) => left.name.localeCompare(right.name));
      setTypes(sorted);
      setTemplates(loadedTemplates);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('consultationTypesLoadFailed', { defaultValue: 'Failed to load consultation types.' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  const templateCountsByType = React.useMemo(() => {
    return templates.reduce<Record<string, number>>((accumulator, template) => {
      if (!template.appointmentTypeId) {
        return accumulator;
      }

      accumulator[template.appointmentTypeId] = (accumulator[template.appointmentTypeId] ?? 0) + 1;
      return accumulator;
    }, {});
  }, [templates]);

  const filteredTypes = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return types.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) {
        return false;
      }

      const templateCount = templateCountsByType[item.id] ?? 0;
      if (usageFilter === 'linked' && templateCount === 0) {
        return false;
      }

      if (usageFilter === 'unused' && templateCount > 0) {
        return false;
      }

      if (!query) {
        return true;
      }

      const relatedTemplateNames = templates
        .filter((template) => template.appointmentTypeId === item.id)
        .map((template) => template.name);

      const haystack = [item.name, item.description, item.status, ...relatedTemplateNames]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [searchTerm, statusFilter, templateCountsByType, templates, types, usageFilter]);

  const updateField = React.useCallback(<K extends keyof TypeEditorState>(key: K, value: TypeEditorState[K]) => {
    setEditorState((current) => ({ ...current, [key]: value }));
  }, []);

  const resetEditor = React.useCallback(() => {
    setSelectedTypeId(null);
    setEditorState(emptyEditorState());
  }, []);

  const handleSelectType = React.useCallback((item: AppointmentType) => {
    setSelectedTypeId(item.id);
    setEditorState(toEditorState(item));
    setError('');
    setStatusMessage('');
  }, []);

  const handleSave = React.useCallback(async () => {
    const name = editorState.name.trim();
    if (!name) {
      setError(t('consultationTypeNameRequired', { defaultValue: 'Consultation type name is required.' }));
      return;
    }

    const parsedDuration = Number(editorState.defaultDurationMinutes);
    if (!Number.isFinite(parsedDuration) || parsedDuration < 5 || parsedDuration > 1440) {
      setError(t('consultationTypeDurationInvalid', { defaultValue: 'Default duration must be between 5 and 1440 minutes.' }));
      return;
    }

    setSaving(true);
    setError('');
    setStatusMessage('');

    try {
      if (selectedTypeId) {
        const updated = await updateConsultationType(selectedTypeId, {
          name,
          defaultDurationMinutes: parsedDuration,
          status: editorState.status,
          description: normalizeOptionalText(editorState.description) ?? '',
          consultationCreationMode: editorState.consultationCreationMode,
          chatBotBookingFlow: editorState.chatBotBookingFlow,
          chatBotInstruction: normalizeOptionalText(editorState.chatBotInstruction) ?? '',
        });

        if (!updated) {
          setError(t('consultationTypeUpdateFailed', { defaultValue: 'Failed to update consultation type.' }));
          return;
        }

        resetEditor();
        setStatusMessage(t('consultationTypeUpdated', { defaultValue: 'Consultation type updated.' }));
        await loadTypes();
        return;
      }

      const created = await createConsultationType({
        name,
        defaultDurationMinutes: parsedDuration,
        status: editorState.status,
        description: normalizeOptionalText(editorState.description),
        consultationCreationMode: editorState.consultationCreationMode,
        chatBotBookingFlow: editorState.chatBotBookingFlow,
        chatBotInstruction: normalizeOptionalText(editorState.chatBotInstruction),
        sortOrder: types.length,
      });

      if (!created) {
        setError(t('consultationTypeCreateFailed', { defaultValue: 'Failed to create consultation type.' }));
        return;
      }

      resetEditor();
      setStatusMessage(t('consultationTypeCreated', { defaultValue: 'Consultation type created.' }));
      await loadTypes();
    } catch (saveError) {
      const fallback = selectedTypeId
        ? t('consultationTypeUpdateFailed', { defaultValue: 'Failed to update consultation type.' })
        : t('consultationTypeCreateFailed', { defaultValue: 'Failed to create consultation type.' });
      setError(saveError instanceof Error ? saveError.message : fallback);
    } finally {
      setSaving(false);
    }
  }, [editorState.chatBotBookingFlow, editorState.chatBotInstruction, editorState.consultationCreationMode, editorState.defaultDurationMinutes, editorState.description, editorState.name, editorState.status, loadTypes, resetEditor, selectedTypeId, t, types.length]);

  const activeCount = React.useMemo(() => types.filter((item) => item.status === 'active').length, [types]);
  const linkedCount = React.useMemo(() => Object.values(templateCountsByType).filter((count) => count > 0).length, [templateCountsByType]);

  return (
    <div className={styles.page}>
      <Breadcrumb
        items={[
          { label: t('consultations', { defaultValue: 'Consultations' }), path: '/consultations' },
          { label: t('consultationTypes', { defaultValue: 'Consultation types' }), path: '/consultations/types' },
        ]}
      />

      <section className={styles.hero}>
        <div>
          <h1 className={styles.title}>{t('consultationTypes', { defaultValue: 'Consultation types' })}</h1>
          <p className={styles.subtitle}>
            {t('consultationTypesIntro', {
              defaultValue: 'Create and review consultation types used when creating consultations, matching templates, and keeping intake flows consistent.',
            })}
          </p>
          <div className={styles.heroStats}>
            <span>{t('consultationTypesActiveCount', { defaultValue: '{{count}} active', count: activeCount })}</span>
            <span>{t('consultationTypesLinkedCount', { defaultValue: '{{count}} linked to templates', count: linkedCount })}</span>
            <span>{t('consultationTypesTemplateCount', { defaultValue: '{{count}} templates', count: templates.length })}</span>
          </div>
        </div>
        <div className={styles.heroActions}>
          <Link className="btn btn-secondary" to="/consultations">
            {t('backToConsultations', { defaultValue: 'Back to consultations' })}
          </Link>
          <Link className="btn btn-secondary" to="/consultations/templates">
            {t('manageTemplates', { defaultValue: 'Manage templates' })}
          </Link>
        </div>
      </section>

      {(error || statusMessage) ? (
        <section className={styles.messages}>
          {error ? <p className={styles.error}>{error}</p> : null}
          {statusMessage ? <p className={styles.success}>{statusMessage}</p> : null}
        </section>
      ) : null}

      <section className={styles.layout}>
        <aside className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2>
              {selectedTypeId
                ? t('editConsultationType', { defaultValue: 'Edit consultation type' })
                : t('addConsultationType', { defaultValue: 'Add consultation type' })}
            </h2>
            {selectedTypeId ? <span className={styles.count}>{selectedTypeId.slice(0, 8)}</span> : null}
          </div>

          <div className={styles.formGrid}>
            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('name', { ns: 'common', defaultValue: 'Name' })}</label>
              <input className="text-input" value={editorState.name} onChange={(event) => updateField('name', event.target.value)} maxLength={200} />
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('defaultDurationMinutes', { defaultValue: 'Default duration (minutes)' })}</label>
              <input className="text-input" type="number" min={5} max={1440} value={editorState.defaultDurationMinutes} onChange={(event) => updateField('defaultDurationMinutes', event.target.value)} />
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('status', { defaultValue: 'Status' })}</label>
              <select className="text-input" value={editorState.status} onChange={(event) => updateField('status', event.target.value as AppointmentTypeStatus)}>
                <option value="active">{t('active', { ns: 'common', defaultValue: 'Active' })}</option>
                <option value="archived">{t('inactive', { defaultValue: 'Archived' })}</option>
              </select>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('consultationCreationMode', { defaultValue: 'Consultation creation mode' })}</label>
              <select
                className="text-input"
                value={editorState.consultationCreationMode}
                onChange={(event) => updateField('consultationCreationMode', event.target.value as ConsultationCreationMode)}
              >
                <option value="single">{t('consultationCreationModeSingle', { defaultValue: 'Single consultation' })}</option>
                <option value="perContact">{t('consultationCreationModePerContact', { defaultValue: 'Per contact' })}</option>
                <option value="perAsset">{t('consultationCreationModePerAsset', { defaultValue: 'Per asset' })}</option>
                <option value="perEmployee">{t('consultationCreationModePerEmployee', { defaultValue: 'Per employee' })}</option>
              </select>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('chatBotBookingFlow', { defaultValue: 'Chatbot booking flow' })}</label>
              <select
                className="text-input"
                value={editorState.chatBotBookingFlow}
                onChange={(event) => updateField('chatBotBookingFlow', event.target.value as ChatBotBookingFlow)}
              >
                <option value="offerAvailableSlots">{t('chatBotBookingFlowOfferSlots', { defaultValue: 'Offer available slots' })}</option>
                <option value="autoNearestSlot">{t('chatBotBookingFlowAutoNearest', { defaultValue: 'Auto-book nearest slot' })}</option>
              </select>
              <p className={styles.itemUsageMuted}>
                {t('chatBotBookingFlowHint', {
                  defaultValue: 'Offer slots lists open times. Auto nearest finds one soonest slot and asks the customer to confirm it.',
                })}
              </p>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('chatBotInstruction', { defaultValue: 'Chatbot instruction' })}</label>
              <textarea
                className="text-input"
                rows={4}
                value={editorState.chatBotInstruction}
                onChange={(event) => updateField('chatBotInstruction', event.target.value)}
                maxLength={4000}
                placeholder={t('chatBotInstructionPlaceholder', {
                  defaultValue: 'Instructions given to the AI when booking this consultation type…',
                })}
              />
              <p className={styles.itemUsageMuted}>
                {t('chatBotInstructionHint', {
                  defaultValue: 'Sent to the AI when a customer books this type. Use for product details, what to collect, and confirmation wording.',
                })}
              </p>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>{t('description', { defaultValue: 'Description' })}</label>
              <textarea className="text-input" rows={3} value={editorState.description} onChange={(event) => updateField('description', event.target.value)} maxLength={1000} />
            </div>
          </div>

          <div className={styles.footer}>
            {selectedTypeId ? (
              <button className="btn btn-secondary" type="button" onClick={resetEditor} disabled={saving}>
                {t('cancelEdit', { defaultValue: 'Cancel edit' })}
              </button>
            ) : (
              <button className="btn btn-secondary" type="button" onClick={resetEditor} disabled={saving}>
                {t('resetForm', { defaultValue: 'Reset form' })}
              </button>
            )}
            <button className="btn btn-primary" type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving
                ? t('saving', { ns: 'common', defaultValue: 'Saving…' })
                : selectedTypeId
                  ? t('saveChanges', { defaultValue: 'Save changes' })
                  : t('addConsultationType', { defaultValue: 'Add consultation type' })}
            </button>
          </div>
        </aside>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <h2>{t('existingConsultationTypes', { defaultValue: 'Existing consultation types' })}</h2>
            <span className={styles.count}>{filteredTypes.length}/{types.length}</span>
          </div>

          <div className={styles.filters}>
            <input
              className="text-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('searchConsultationTypes', { defaultValue: 'Search consultation types' })}
            />
            <select className="text-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | AppointmentTypeStatus)}>
              <option value="all">{t('allStatuses', { defaultValue: 'All statuses' })}</option>
              <option value="active">{t('activeOnly', { defaultValue: 'Active only' })}</option>
              <option value="archived">{t('inactiveOnly', { defaultValue: 'Archived only' })}</option>
            </select>
            <select className="text-input" value={usageFilter} onChange={(event) => setUsageFilter(event.target.value as 'all' | 'linked' | 'unused')}>
              <option value="all">{t('allUsageStates', { defaultValue: 'All usage states' })}</option>
              <option value="linked">{t('linkedToTemplates', { defaultValue: 'Linked to templates' })}</option>
              <option value="unused">{t('unusedTypes', { defaultValue: 'Unused types' })}</option>
            </select>
          </div>

          {loading ? (
            <Spinner />
          ) : filteredTypes.length === 0 ? (
            <div className={styles.empty}>{t('consultationTypesNoMatch', { defaultValue: 'No consultation types match the current filters.' })}</div>
          ) : (
            <div className={styles.list}>
              {filteredTypes.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.item} ${item.id === selectedTypeId ? styles.itemActive : ''}`}
                  onClick={() => handleSelectType(item)}
                >
                  <div className={styles.itemHeader}>
                    <strong>{item.name}</strong>
                    <span className={`${styles.badge} ${item.status === 'active' ? styles.badgeActive : styles.badgeMuted}`}>
                      {item.status === 'active' ? t('active', { ns: 'common', defaultValue: 'Active' }) : t('inactive', { defaultValue: 'Archived' })}
                    </span>
                  </div>
                  <div className={styles.itemMeta}>
                    <span>{t('defaultDurationMinutes', { defaultValue: 'Duration' })}: {item.defaultDurationMinutes}m</span>
                    <span>{t('consultationCreationMode', { defaultValue: 'Creation mode' })}: {getConsultationCreationModeLabel(item.consultationCreationMode, t)}</span>
                    <span>{t('chatBotBookingFlow', { defaultValue: 'Chatbot booking' })}: {getChatBotBookingFlowLabel(item.chatBotBookingFlow, t)}</span>
                    <span>{t('updated', { ns: 'common', defaultValue: 'Updated' })}: {formatDateTime(item.updatedAt)}</span>
                    <span>{t('templateUsage', { defaultValue: 'Templates' })}: {templateCountsByType[item.id] ?? 0}</span>
                  </div>
                  <p className={styles.itemDescription}>{item.description || t('noDescription', { defaultValue: 'No description' })}</p>
                  {(templateCountsByType[item.id] ?? 0) > 0 ? (
                    <p className={styles.itemUsage}>
                      {templates
                        .filter((template) => template.appointmentTypeId === item.id)
                        .slice(0, 3)
                        .map((template) => template.name)
                        .join(', ')}
                      {((templateCountsByType[item.id] ?? 0) > 3) ? ` +${(templateCountsByType[item.id] ?? 0) - 3}` : ''}
                    </p>
                  ) : (
                    <p className={styles.itemUsageMuted}>{t('consultationTypeNoTemplates', { defaultValue: 'No consultation templates are currently linked to this type.' })}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      </section>
    </div>
  );
};

export default ConsultationTypesPage;
